import React, { useState } from 'react';
import { Box, Paper, Typography, Divider, Tabs, Tab, styled, Button, Grid, useTheme, useMediaQuery, FormControl, Select, MenuItem } from '@mui/material';
// PersonelListesi artık kullanılmıyor - Vardiya Listesi içinden erişilebilir
import VardiyaListesi from './VardiyaListesi';
import GirisCikisRaporlari from './GirisCikisRaporlari';
import ToplamSure from './ToplamSure';
import OkutmaKayitlari from './OkutmaKayitlari';
import { Download as DownloadIcon, QrCode as QrCodeIcon, Schedule as ScheduleIcon, AssignmentTurnedIn as ReportIcon, Timer as TimerIcon, Palette as PaletteIcon, AccessTime as AccessTimeIcon, FactCheck as ScanLogIcon } from '@mui/icons-material';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useAuth } from '../../contexts/AuthContext';
import VardiyaQrPrintModal from './VardiyaQrPrintModal';
import VardiyaQrIndirModal from './VardiyaQrIndirModal';
import GuideButton from '../common/guide/GuideButton';
import { useTranslation } from 'react-i18next';

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

// Kaydırılabilir ana içerik için styled component - Mobilde scroll yok
const ScrollableContent = styled(Box)(({ theme }) => ({
  // Desktop'ta sabit yükseklik ve scroll
  [theme.breakpoints.up('md')]: {
    height: 'calc(100vh - 150px)',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
  // Mobilde scroll yok, içerik akıyor
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    overflowY: 'visible',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vardiya-tabpanel-${index}`}
      aria-labelledby={`vardiya-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vardiya-tab-${index}`,
    'aria-controls': `vardiya-tabpanel-${index}`,
  };
}

interface ShiftsProps {
  branchId?: string;
  isManager?: boolean;
}

const Shifts: React.FC<ShiftsProps> = ({ branchId, isManager = false }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = useState(0);
  const [exporting, setExporting] = useState(false);
  const { userDetails } = useAuth();
  const [qrDesignModalOpen, setQrDesignModalOpen] = useState(false);
  const [qrDownloadModalOpen, setQrDownloadModalOpen] = useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Geçerli sekmeye göre başlık belirleme
  const getTabTitle = () => {
    switch (value) {
      case 0:
        return tr("Vardiya Listesi", "Shift List");
      case 1:
        return tr("Giriş Çıkış Raporları", "Check-In / Check-Out Reports");
      case 2:
        return tr("Toplam Süre", "Total Duration");
      case 3:
        return tr("Okutma Kayıtları", "Scan Records");
      default:
        return tr("Vardiya Yönetimi", "Shift Management");
    }
  };

  // Excel'e veri aktarma işlevi
  const exportToExcel = async () => {
    try {
      setExporting(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(getTabTitle());
      
      // Aktif sekmeye göre farklı sütun başlıkları ve veriler hazırla
      // Tab 0: Vardiya Listesi
      // Tab 1: Giriş Çıkış Raporları
      // Tab 2: Toplam Süre
      if (value === 0) { // Vardiya Listesi
        worksheet.columns = [
          { header: tr('Vardiya Adı', 'Shift Name'), key: 'name', width: 30 },
          { header: tr('Giriş Saati', 'Start Time'), key: 'startTime', width: 15 },
          { header: tr('Çıkış Saati', 'End Time'), key: 'endTime', width: 15 },
          { header: tr('Personel Sayısı', 'Personnel Count'), key: 'personnelCount', width: 15 },
          { header: tr('Geç Kalma Toleransı', 'Late Tolerance'), key: 'lateTolerance', width: 20 },
          { header: tr('Erken Çıkma Toleransı', 'Early Exit Tolerance'), key: 'earlyExitTolerance', width: 20 }
        ];
        
        // Özel olarak veri ekleme işlemi için event oluşturup yayınlanacak
        const event = new CustomEvent('export-vardiya-to-excel', { 
          detail: { 
            worksheet,
            branchId,
            isManager
          } 
        });
        window.dispatchEvent(event);
      }
      else if (value === 1) { // Giriş Çıkış Raporları
        worksheet.columns = [
          { header: tr('Personel', 'Personnel'), key: 'personnel', width: 30 },
          { header: tr('Vardiya', 'Shift'), key: 'shift', width: 20 },
          { header: tr('Tarih', 'Date'), key: 'date', width: 15 },
          { header: tr('Giriş Saati', 'Start Time'), key: 'checkIn', width: 15 },
          { header: tr('Çıkış Saati', 'End Time'), key: 'checkOut', width: 15 },
          { header: tr('Toplam Süre', 'Total Duration'), key: 'totalHours', width: 15 },
          { header: tr('Durum', 'Status'), key: 'status', width: 15 }
        ];
        
        // Özel olarak veri ekleme işlemi için event oluşturup yayınlanacak
        const event = new CustomEvent('export-report-to-excel', { 
          detail: { 
            worksheet,
            branchId,
            isManager
          } 
        });
        window.dispatchEvent(event);
      }
      else if (value === 2) { // Toplam Süre
        worksheet.columns = [
          { header: tr('Personel', 'Personnel'), key: 'personnel', width: 30 },
          { header: tr('Dönem', 'Period'), key: 'period', width: 20 },
          { header: tr('Çalışma Saati', 'Working Time'), key: 'workHours', width: 15 },
          { header: tr('Ek Çalışma', 'Extra Work'), key: 'extraWork', width: 15 },
          { header: tr('Toplam Süre', 'Total Duration'), key: 'totalHours', width: 15 }
        ];
        
        // Özel olarak veri ekleme işlemi için event oluşturup yayınlanacak
        const event = new CustomEvent('export-toplamsure-to-excel', { 
          detail: { 
            worksheet,
            branchId,
            isManager
          } 
        });
        window.dispatchEvent(event);
      }
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) { // başlığı atla
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Excel dosyasını oluştur ve indir
      setTimeout(async () => {
        const buffer = await workbook.xlsx.writeBuffer();
        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        saveAs(new Blob([buffer]), `${getTabTitle()}_${currentDate}.xlsx`);
        setExporting(false);
      }, 500); // İlgili komponentın veri eklemesine zaman tanımak için küçük bir gecikme
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      alert(tr('Excel dosyası oluşturulurken bir hata oluştu.', 'An error occurred while creating the Excel file.'));
      setExporting(false);
    }
  };

  // QR kod indirme işlevi (Tek QR / single — yönsüz; companyId gömülü)
  return (
    <Paper sx={{ 
      p: { xs: 1, md: 2 }, 
      mt: { xs: 1, md: 2 }, 
      height: { xs: 'auto', md: 'calc(100vh - 120px)' }, 
      minHeight: { xs: 'auto', md: 'calc(100vh - 120px)' },
      display: 'flex', 
      flexDirection: 'column' 
    }}>
      <HeaderContainer 
        sx={{ 
          flexDirection: { xs: 'column', md: 'row' }, 
          alignItems: { xs: 'flex-start', md: 'center' }, 
          gap: { xs: 1.5, md: 0 } 
        }}
      >
        {/* Başlık */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <AccessTimeIcon sx={{ fontSize: { xs: 24, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {isManager ? tr('Şube ', 'Branch ') : ''}{getTabTitle()}
          </Typography>
          {/* Mobile: Sadece info butonu başlık yanında */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto' }}>
            <GuideButton guideKey="shifts.list" color="primary" size="small" />
          </Box>
        </Box>

        {/* Desktop: Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          <GuideButton guideKey="shifts.list" color="primary" sx={{ mr: 0.5 }} />
          <Button
            variant="contained"
            color="success"
            startIcon={<QrCodeIcon />}
            onClick={() => setQrDownloadModalOpen(true)}
            disabled={!userDetails?.companyId && !branchId}
            size="small"
          >
            {isManager ? tr('Şube QR', 'Branch QR') : tr('QR İndir', 'Download QR')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<PaletteIcon />}
            onClick={() => setQrDesignModalOpen(true)}
            size="small"
          >
            {tr('QR Tasarla', 'Design QR')}
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            disabled={exporting || value === 3}
            size="small"
          >
            {exporting ? tr('İndiriliyor...', 'Downloading...') : tr('Excel İndir', 'Download Excel')}
          </Button>
        </Box>

        {/* Mobile: Grid Butonlar */}
        <Grid 
          container 
          spacing={1} 
          sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            width: '100%' 
          }}
        >
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<QrCodeIcon />}
              onClick={() => setQrDownloadModalOpen(true)}
              disabled={!userDetails?.companyId && !branchId}
              size="small"
            >
              {isManager ? tr('Şube QR', 'Branch QR') : tr('QR İndir', 'Download QR')}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              startIcon={<PaletteIcon />}
              onClick={() => setQrDesignModalOpen(true)}
              size="small"
            >
              {tr('QR Tasarla', 'Design QR')}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="info"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={exporting || value === 3}
              size="small"
            >
              {exporting ? tr('İndiriliyor...', 'Downloading...') : tr('Excel İndir', 'Download Excel')}
            </Button>
          </Grid>
        </Grid>
      </HeaderContainer>
      <Divider sx={{ mb: 1 }} />
      
      <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' } }}>
        {/* Desktop: Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label={tr('vardiya yönetim tabları', 'shift management tabs')}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: '40px' }}
          >
            <Tab label={tr('Vardiya Listesi', 'Shift List')} {...a11yProps(0)} sx={{ py: 0.5 }} />
            <Tab label={tr('Giriş Çıkış Raporları', 'Check-In / Check-Out Reports')} {...a11yProps(1)} sx={{ py: 0.5 }} />
            <Tab label={tr('Toplam Süre', 'Total Duration')} {...a11yProps(2)} sx={{ py: 0.5 }} />
            <Tab label={tr('Okutma Kayıtları', 'Scan Records')} {...a11yProps(3)} sx={{ py: 0.5 }} />
          </Tabs>
        </Box>

        {/* Mobile: Dropdown Menü */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }}>
          <FormControl fullWidth size="small">
            <Select
              id="vardiya-tab-select"
              value={value}
              onChange={(e) => setValue(e.target.value as number)}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1
                }
              }}
            >
              <MenuItem value={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography>{tr('Vardiya Listesi', 'Shift List')}</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReportIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography>{tr('Giriş Çıkış Raporları', 'Check-In / Check-Out Reports')}</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimerIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography>{tr('Toplam Süre', 'Total Duration')}</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScanLogIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography>{tr('Okutma Kayıtları', 'Scan Records')}</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <ScrollableContent>
          <TabPanel value={value} index={0}>
            <VardiyaListesi branchId={branchId} isManager={isManager} />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <GirisCikisRaporlari branchId={branchId} isManager={isManager} />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <ToplamSure branchId={branchId} isManager={isManager} />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <OkutmaKayitlari branchId={branchId} isManager={isManager} />
          </TabPanel>
        </ScrollableContent>
      </Box>

      {/* QR Tasarım Modal'ı */}
      <VardiyaQrPrintModal 
        open={qrDesignModalOpen} 
        onClose={() => setQrDesignModalOpen(false)}
        companyId={userDetails?.companyId}
        branchId={branchId}
      />

      {/* QR İndir Modal'ı (şube seçimi + Giriş/Çıkış/Tek QR) */}
      <VardiyaQrIndirModal
        open={qrDownloadModalOpen}
        onClose={() => setQrDownloadModalOpen(false)}
        companyId={userDetails?.companyId}
        branchId={branchId}
        isManager={isManager}
      />
    </Paper>
  );
};

export default Shifts; 